import React from 'react';
import { Github, Linkedin, Mail, Heart } from 'lucide-react';
import { usePortfolioData } from '../contexts/PortfolioDataContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { profile: personalInfo } = usePortfolioData();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const footerLinks = [
    { label: 'Home', id: 'home' },
    { label: 'About', id: 'about' },
    { label: 'Projects', id: 'projects' },
    { label: 'Skills', id: 'skills' },
    { label: 'Experience', id: 'experience' },
    { label: 'Contact', id: 'contact' }
  ];

  if (!personalInfo) {
    return null;
  }

  return (
    <footer className="theme-page-gradient border-t" style={{ borderColor: 'var(--site-border-soft)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Brand */}
          <div>
            <h3 className="text-2xl font-bold theme-text-primary theme-heading-font mb-4">
              {personalInfo.name}
            </h3>
            <p className="theme-text-secondary mb-4 break-words">
              {personalInfo.tagline || 'Portfolio website built for showcasing projects and experience.'}
            </p>
            <div className="flex gap-4">
              <a
                href={`mailto:${personalInfo.email}`}
                className="w-10 h-10 rounded-lg flex items-center justify-center theme-surface-strong theme-icon-button transition-all"
                aria-label="Email"
              >
                <Mail size={20} />
              </a>
              <a
                href={personalInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg flex items-center justify-center theme-surface-strong theme-icon-button transition-all"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-lg flex items-center justify-center theme-surface-strong theme-icon-button transition-all"
                aria-label="GitHub"
              >
                <Github size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold theme-text-primary theme-heading-font mb-4">
              Quick Links
            </h4>
            <ul className="space-y-2">
              {footerLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="theme-text-secondary hover:[color:var(--site-accent-primary)] transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold theme-text-primary theme-heading-font mb-4">
              Contact
            </h4>
            <ul className="space-y-2 theme-text-secondary">
              <li>
                <a href={`mailto:${personalInfo.email}`} className="hover:[color:var(--site-accent-primary)] transition-colors">
                  <span className="break-all">{personalInfo.email}</span>
                </a>
              </li>
              <li>
                <a href={`tel:${personalInfo.phone}`} className="hover:[color:var(--site-accent-primary)] transition-colors">
                  {personalInfo.phone}
                </a>
              </li>
              <li>{personalInfo.location}</li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t" style={{ borderColor: 'var(--site-border-soft)' }}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="theme-text-secondary text-sm">
              © {currentYear} {personalInfo.name}. All rights reserved.
            </p>
            <p className="theme-text-secondary text-sm flex items-center gap-1">
              Made with <Heart size={16} className="fill-current theme-accent-text" />
              {personalInfo.footerBuiltWith?.trim() ? ` using ${personalInfo.footerBuiltWith.trim()}` : ''}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
